import {
    Children,
    cloneElement,
    createContext,
    type FC,
    type KeyboardEvent as ReactKeyboardEvent,
    type PropsWithChildren,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";

import { css } from "@compiled/react";
import {
    type IntersectionOptions,
    useInView
    // @ts-ignore
} from "react-intersection-observer";

import { token } from "@atlaskit/tokens";

import { Initializer } from "@/analytics/atl-analytics/initializer";

import {
    deepForEach,
    deepMap,
    isTab,
    isTabList,
    isTabPanel
} from "./tab-helper";

export type TabsContextType = {
    currentTab: number;
    /**
     * User has selected a tab
     */
    userSelected: boolean;
    unsetUserSelected: () => void;
    isCurrentTab: (tabIndex?: number) => boolean;
    goToTab: (tabIndex?: number) => void;
    goToNextTab: () => void;
    goToPrevTab: () => void;
    goToFirstTab: () => void;
    goToLastTab: () => void;
    onKeyDown: (e: ReactKeyboardEvent) => void;
    tabsContainerIsVisible: boolean;
    tabListScrollRef: (node?: Element | null) => void;
    tabListIsVisible: boolean;
};

const TabsContext = createContext<TabsContextType | null>(null);

export const useTabsContext = () => {
    const context = useContext(TabsContext);
    if (!context) {
        throw new Error("useTabsContext must be used within Tabs");
    }
    return context;
};

type TabsProps = PropsWithChildren<{
    id?: string;
    initialTab?: number;
    loop?: boolean;
    /**
     * An ordered list of tab IDs that maps each tab index to a string ID.
     * When provided, the active tab index will be synced to/from the URL
     * querystring parameter `tab` using `history.replaceState`.
     */
    tabIds?: string[];
    /**
     * Custom options for the useInView hook
     */
    useInViewOptionsContainer?: IntersectionOptions;
}>;

let nextId = 0;

function getTabIndexFromUrl(tabIds: string[]): number | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (!tabParam) return null;
    const index = tabIds.indexOf(tabParam);
    return index !== -1 ? index : null;
}

export const Tabs: FC<TabsProps> = ({
    id,
    initialTab,
    loop = true,
    tabIds: externalTabIds,
    children,
    useInViewOptionsContainer
}: TabsProps) => {
    const resolvedInitialTab = (() => {
        if (externalTabIds && externalTabIds.length > 0) {
            const urlIndex = getTabIndexFromUrl(externalTabIds);
            if (urlIndex !== null) return urlIndex;
        }
        return initialTab ?? 0;
    })();

    const [currentTab, setCurrentTab] = useState<number>(resolvedInitialTab);
    const [userSelected, setUserSelected] = useState<boolean>(false);

    const [focus, setFocus] = useState<boolean>(false);

    const tabIds = useRef<string[]>([]);
    const contextId = useRef(`:tabs:${id ? id : nextId++}:`);
    const DEFAULT_IN_VIEW_THRESHOLD = 0.3;

    const { ref: containerScrollRef, inView: tabsContainerIsVisible } =
        useInView({
            threshold: DEFAULT_IN_VIEW_THRESHOLD,
            ...useInViewOptionsContainer
        });

    const { ref: tabListScrollRef, inView: tabListIsVisible } = useInView({
        threshold: DEFAULT_IN_VIEW_THRESHOLD
    });

    function getTabsCount() {
        let count = 0;
        deepForEach(children, (child) => {
            if (isTab(child)) {
                count++;
            }
        });
        return count;
    }

    function getChildren() {
        const tabsCount = getTabsCount();
        let panelIndex = 0;
        tabIds.current = tabIds.current || [];
        let diff = tabIds.current.length - tabsCount;

        // Add ids if new tabs have been added
        // Don't bother removing ids, just keep them in case they are added again
        // This is more efficient, and keeps the uuid counter under control
        while (diff++ < 0) {
            tabIds.current.push(`${contextId.current}${tabIds.current.length}`);
        }
        // Map children to dynamically setup refs
        return deepMap(children, (child) => {
            let result = child;

            // Check if the window is available
            const windowAvailable =
                !!window &&
                !!window.document &&
                !!window.document.activeElement;
            // Get the active element id
            const activeElementId = windowAvailable
                ? window.document.activeElement?.id
                : null;

            // Clone TabList and Tab components to have refs
            if (isTabList(child)) {
                let listIndex = 0;

                // Figure out if the current focus in the DOM is set on a Tab
                // If it is we should keep the focus on the next selected tab
                let wasTabFocus = false;
                if (windowAvailable && activeElementId) {
                    // Check if the active element is a tab
                    wasTabFocus = Children.toArray(
                        (child as ReactElement).props.children
                    )
                        .filter(isTab)
                        .some((_, i) => {
                            return (
                                activeElementId === `tab${tabIds.current[i]}`
                            );
                        });
                }

                result = cloneElement(child as ReactElement, {
                    children: deepMap(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (child as ReactElement<any, any>).props.children,
                        (tab) => {
                            const selected = currentTab === listIndex;

                            const props = {
                                id: tabIds.current[listIndex],
                                tabIndex: listIndex,
                                focus: selected && (focus || wasTabFocus)
                            };

                            listIndex++;
                            return cloneElement(tab as ReactElement, props);
                        }
                    )
                });
            } else if (isTabPanel(child)) {
                // Preserve existing props (like hideTabPanelRole) when cloning
                const existingProps = (child as ReactElement).props || {};
                const props = {
                    ...existingProps,
                    id: tabIds.current[panelIndex],
                    panelIndex
                };

                panelIndex++;

                result = cloneElement(child as ReactElement, props);
            }

            return result;
        });
    }

    const unsetUserSelected = () => {
        setUserSelected(false);
    };

    const goToTab = (tabIndex?: number) => {
        if (tabIndex === undefined) {
            return;
        }
        setCurrentTab(tabIndex);
        setUserSelected(true);

        if (
            externalTabIds &&
            externalTabIds.length > tabIndex &&
            typeof window !== "undefined"
        ) {
            const params = new URLSearchParams(window.location.search);
            params.set("tab", externalTabIds[tabIndex] as string);
            const newUrl = `${window.location.pathname}?${params.toString()}${
                window.location.hash
            }`;
            window.history.replaceState(null, "", newUrl);
        }
    };

    const isCurrentTab = (tabIndex?: number) => {
        return tabIndex === currentTab;
    };

    const goToNextTab = useCallback(() => {
        if (currentTab + 1 < tabIds.current.length) {
            goToTab(currentTab + 1);
        } else if (loop) {
            goToTab(0);
        }
    }, [loop, currentTab]);

    const goToPrevTab = useCallback(() => {
        if (currentTab - 1 >= 0) {
            goToTab(currentTab - 1);
        } else if (loop) {
            goToTab(tabIds.current.length - 1);
        }
    }, [loop, currentTab]);

    const goToFirstTab = () => {
        goToTab(0);
    };

    const goToLastTab = () => {
        goToTab(tabIds.current.length - 1);
    };

    const onKeyDown = (e: ReactKeyboardEvent) => {
        if (e.code === "ArrowLeft" || e.code === "ArrowUp") {
            goToPrevTab();
        } else if (e.code === "ArrowRight" || e.code === "ArrowDown") {
            goToNextTab();
        } else if (e.code === "End") {
            // Select last tab (End key)
            goToFirstTab();
        } else if (e.code === "Home") {
            // Select first tab (Home key)
            goToLastTab();
        } else {
            return;
        }

        setUserSelected(true);
        setFocus(true);

        // This prevents scrollbars from moving around
        e.preventDefault();
    };

    // Reset the userSelected state when the section is visible again
    useEffect(() => {
        if (tabsContainerIsVisible) {
            setUserSelected(false);
        }
    }, [tabsContainerIsVisible]);

    // Reinitialize analytics when the tab changes
    // this is needed because the content wasn't rendered before
    useEffect(() => {
        Initializer.autoTrack();
    }, [currentTab]);

    const containerStyles = css({
        display: "flex",
        flexDirection: "column",
        gap: token("space.500"),
        overflow: "hidden"
    });

    return (
        <TabsContext.Provider
            value={{
                currentTab,
                userSelected,
                unsetUserSelected,
                goToTab,
                isCurrentTab,
                goToNextTab,
                goToPrevTab,
                goToFirstTab,
                goToLastTab,
                onKeyDown,
                tabsContainerIsVisible,
                tabListIsVisible,
                tabListScrollRef
            }}
        >
            <div ref={containerScrollRef} css={containerStyles}>
                {getChildren()}
            </div>
        </TabsContext.Provider>
    );
};
